import React from 'react';
import { AlertTriangle, XCircle, Info, Lock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

// Human-interpretable error messages
const ERROR_MESSAGES = {
  LIBMAGIC_MISSING: {
    title: 'File Validation Temporarily Unavailable',
    message: 'A server component needed to verify file types is currently unavailable. Your document has been uploaded, but we cannot confirm its format at this time.',
    action: 'Please contact support if this issue persists. Your data is safe.',
    icon: AlertTriangle,
    color: 'amber'
  },
  NOT_AUTHORIZED_FOR_FORMAT: {
    title: 'This Export Format Requires an Upgrade',
    message: (detail) => `You attempted to export to ${detail?.requested_format || 'a format'} which is not available in your ${detail?.tier || 'current'} tier.`,
    action: (detail) => `Upgrade to ${detail?.required_tier || 'a higher tier'} to unlock this format.`,
    icon: Lock,
    color: 'blue',
    cta: 'View Upgrade Options'
  },
  PARSING_ERROR: {
    title: 'We Could Not Read This Document',
    message: 'The document structure could not be understood. This may happen with corrupted files, unusual formats, or password-protected documents.',
    action: 'Try re-saving the document in a standard format (PDF, DOCX) and uploading again.',
    icon: XCircle,
    color: 'red'
  },
  AUTHENTICATION_FAILED: {
    title: 'Your Session Has Expired',
    message: 'For your security, we logged you out after a period of inactivity.',
    action: 'Please log in again to continue.',
    icon: Info,
    color: 'blue',
    cta: 'Log In'
  },
  NETWORK_ERROR: {
    title: 'Connection Issue',
    message: 'We are having trouble reaching our servers. This is usually temporary.',
    action: 'Check your internet connection and try again in a moment.',
    icon: AlertTriangle,
    color: 'amber'
  },
  VALIDATION_ERROR: {
    title: 'Information Required',
    message: (detail) => detail?.message || 'Some required information is missing or incorrect.',
    action: 'Please review the form and try again.',
    icon: Info,
    color: 'blue'
  },
  RATE_LIMIT_EXCEEDED: {
    title: 'Too Many Requests',
    message: 'You have made too many requests in a short time. This is a security measure.',
    action: 'Please wait a moment before trying again.',
    icon: AlertTriangle,
    color: 'amber'
  },
  SERVER_ERROR: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred on our servers. This has been logged and our team will investigate.',
    action: 'Please try again. If the problem persists, contact support.',
    icon: XCircle,
    color: 'red'
  }
};

// Map HTTP status codes to error types
const STATUS_TO_ERROR_TYPE = {
  401: 'AUTHENTICATION_FAILED',
  403: 'NOT_AUTHORIZED_FOR_FORMAT',
  429: 'RATE_LIMIT_EXCEEDED',
  500: 'SERVER_ERROR',
  503: 'LIBMAGIC_MISSING'
};

export default function ErrorDisplay({ error, onRetry, onUpgrade, onLogin }) {
  if (!error) return null;

  // Extract error details
  let errorType = 'SERVER_ERROR';
  let errorDetail = null;

  if (error.response) {
    // HTTP error response
    const status = error.response.status;
    const data = error.response.data;

    // Check for error code in response
    if (data?.detail?.code) {
      errorType = data.detail.code;
      errorDetail = data.detail;
    } else if (data?.code) {
      errorType = data.code;
      errorDetail = data;
    } else {
      // Fallback to status code mapping
      errorType = STATUS_TO_ERROR_TYPE[status] || 'SERVER_ERROR';
      errorDetail = data?.detail || data;
    }
  } else if (error.request) {
    // Network error
    errorType = 'NETWORK_ERROR';
  } else {
    // Other error
    errorDetail = { message: error.message };
  }

  const errorConfig = ERROR_MESSAGES[errorType] || ERROR_MESSAGES.SERVER_ERROR;
  const Icon = errorConfig.icon;
  const colorClasses = {
    red: 'bg-red-50 border-red-200 text-red-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900'
  };

  const message = typeof errorConfig.message === 'function' 
    ? errorConfig.message(errorDetail) 
    : errorConfig.message;
  
  const action = typeof errorConfig.action === 'function' 
    ? errorConfig.action(errorDetail) 
    : errorConfig.action;

  return (
    <Alert className={`${colorClasses[errorConfig.color]} border`}>
      <Icon className="h-5 w-5" />
      <AlertTitle className="font-semibold text-base mb-2">
        {errorConfig.title}
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <p className="text-sm">{message}</p>
        <p className="text-sm font-medium">{action}</p>
        
        {/* Allowed formats for tier errors */}
        {errorType === 'NOT_AUTHORIZED_FOR_FORMAT' && errorDetail?.allowed_formats && (
          <div className="mt-3 pt-3 border-t border-current/20">
            <p className="text-xs font-medium mb-1">Your current tier includes:</p>
            <p className="text-xs">{errorDetail.allowed_formats}</p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex space-x-2 mt-4">
          {errorConfig.cta && errorType === 'NOT_AUTHORIZED_FOR_FORMAT' && onUpgrade && (
            <Button onClick={onUpgrade} size="sm" className="bg-blue-600 hover:bg-blue-700">
              {errorConfig.cta}
            </Button>
          )}
          {errorConfig.cta && errorType === 'AUTHENTICATION_FAILED' && onLogin && (
            <Button onClick={onLogin} size="sm" className="bg-blue-600 hover:bg-blue-700">
              {errorConfig.cta}
            </Button>
          )}
          {onRetry && errorType !== 'NOT_AUTHORIZED_FOR_FORMAT' && errorType !== 'AUTHENTICATION_FAILED' && (
            <Button onClick={onRetry} size="sm" variant="outline">
              Try Again
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}

// Simplified version for inline use
export function InlineError({ error, className = '' }) {
  if (!error) return null;

  let message = 'An error occurred';
  if (typeof error === 'string') {
    message = error;
  } else if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    message = typeof detail === 'string' ? detail : detail.message || message;
  } else if (error.message) {
    message = error.message;
  }

  return (
    <div className={`flex items-start text-red-600 text-sm ${className}`}>
      <XCircle className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}
